
import { Component, Vue, Prop, Watch, Emit } from "vue-property-decorator";
import { namespace, Mutation, Action, Getter } from "vuex-class";
import { UserPermissions } from "@/store/types";
import vSelect from "vue-select";
import { Datetime } from "vue-datetime";
import { ActivityLogDTO, SelectedSite } from "../../store/activity-log/types";
import { SiteTreeNode } from "@/store/eventqueue/types";
import { formatDateMixin } from "@/mixins";
import AreaFinder from "@/components/AreaFinder.vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import {
	AlarmQueueFilterID,
	FilteredEvent,
	AlarmQueueFilter,
	AlarmQueueFilterEditModel
} from "@/store/eventqueue/types";
import { watch } from "fs";

const ActivityLog = namespace("activityLog");

Vue.mixin(formatDateMixin);

const { formatDateStr } = formatDateMixin.methods;
const Reports = namespace("reports");

@Component({
	components: {
		"v-select": vSelect,
		"area-finder": AreaFinder,
		Datetime,
		VuePerfectScrollbar: VuePerfectScrollbar
	}
})
export default class ActivityLogTable extends Vue {
	@Action fetchUserGroups: any;
	@ActivityLog.Action getActivityLog: any;
	@ActivityLog.Action getActivityLogEntry: any;
	@ActivityLog.Action getActivityLogOutcomes: any;
	@ActivityLog.Action addActivityLog: any;
	@ActivityLog.Action editActivityLog: any;
	@ActivityLog.Action hideActivityLog: any;
	@ActivityLog.Action unHideActivityLog: any;
	@ActivityLog.Getter ("getSelectedSites") selectedSites:SelectedSite[];
	@ActivityLog.Mutation addSelectedSite: (site: SelectedSite) => void;
	@ActivityLog.Mutation removeSelectedSite: (site: SelectedSite) => void;
	@Reports.Action fetchReportServiceEndpoint: any;
	@Reports.Action getAvailableReportTemplatesForArea: any;

	@Getter("getUserGroups") userGroups: any;
	@Getter getPermissions: any;
	@Getter getUserId: number;
	@Getter getUserGroupId: number;

	@Prop() liveData: boolean = true;
	@Prop() fromDate: null | Date = null;
	@Prop() toDate: null | Date = null;
	@Prop() triggerUpdate: boolean = false;

	@Watch("triggerUpdate")
	onTriggerUpdateChanged(newVal, oldVal) {
		if (this.triggerUpdate == true) {
			this.fromDateValue = this.fromDate;
			this.toDateValue = this.toDate;
			this.getActivityLogList();
			this.$emit("Updated", true);
		}
	}

	@Watch("liveData")
	onLiveDataChanged(newVal, oldVal) {
		if (this.liveData == true) {
			this.fromDateValue = null;
			this.toDateValue = null;
			this.PageNumber = 1;
			this.getActivityLogListLive();
		}
	}

	private FromDateTime;

	private ToDateTime;

	private fromDateValue = null;

	private toDateValue = null;

	// if we tuncate the outcome note or not
	private truncateLogs = true;

	// number of characters to truncate after
	private stringTruncateLenth = 130;

	private loading = false;

	// time period to check for new activity logs
	private updateInterval: number = 1500;

	// variable to hold SetTimeout object
	private timeOut = null;

	// variable to hold the list of activity logs
	private TableData: ActivityLogDTO[] = [];

	// list of the available areas the user has access to
	private availableAreas = [];

	// list of the available categories (event outcomes)
	private CategoryData = [];

	// object used to hold data while adding or editing an activity log
	private EditRowData: ActivityLogDTO = {
		activityLogID: 0,
		eventTypeID: null,
		logged: new Date(),
		start: new Date(),
		end: null,
		categoryID: null,
		categoryTitle: null,
		areaID: null,
		areaTitle: null,
		viewedUserID: null,
		outcomeNote: null,
		userTitle: null,
		hiddenFromUsers: false
	};

	// status of editing or adding a record, used to display the edit interface
	private EditingRecord = false;

	// variable to hold the id of the record being edited
	private CurrentActivityBeingEdited = 0;

	// variable to hold the HTML element for the editor row
	private EditorRow;

	// variable to hold users timezone
	private TimeZone = null;

	private CanRaiseActivityLog = false;
	private CanToggleLogVisibility = false;
	private CanEditOwnActivityLog = false;
	private CanEditOtherActivityLogs = false;
	private CurrentUserId = 0;

	private PageNumber = 1;
	private TotalPages = 1;

	// default css class to set the font size on the activity log table
	private fontSizeClass = "font-large";

	// flag used to determin if we are hiding or showing all data columns in the table
	private ShowDetailedScreen = false;

	// variable used to hold the API endpoint for the reports server
	private ReportEndPoint = null;

	// bool used for allowing downloads of report
	private get ReportDownloadAllowed() {
		return this.getPermissions.canViewActivityLogManagement && this.getPermissions.canViewReports;
	}

	// variable to hold an array of the areas that the activity log can be filtered on
	// these are displayed as tabs across the top of the table
	private FilterableAreas:SelectedSite[] = [];

	// Id of the current applied filter 0 = none
	private FilteredGroupId = 0;

	// title of the current applied filter
	private FilteredGroupTitle = "All"; // Default title for the ALL tab

	// variable to hold the list of available templates for downloading a report
	private ReportTemplates = [];

	// variable to hold the selected template object
	private SelectedReportTemplate = null;

	// variable that controls whether the Site selection modal is shown
	private SiteSelectorModalShown:boolean = false;

	async created() {
		await this.Run();
	}

	public get eventUrl(){
		return window.location.pathname + '#/eventsearch/';
	}

	// starts off the live activity log
	private async Run() {
		// set up permissions
		this.CanRaiseActivityLog = this.getPermissions.canRaiseActivityLog;
		this.CanToggleLogVisibility = this.getPermissions.canToggleLogVisibility;
		this.CanEditOwnActivityLog = this.getPermissions.canEditOwnActivityLog;
		this.CanEditOtherActivityLogs = this.getPermissions.canEditOtherActivityLogs;

		// set up report download options (if allowed)
		if (this.ReportDownloadAllowed) {
			let endpoint = await this.fetchReportServiceEndpoint();

			if (endpoint && endpoint.data) {
				this.ReportEndPoint = endpoint.data;

				let templates = await this.getAvailableReportTemplatesForArea(this.getUserGroupId);

				// default the selected report if there is only 1 available
				if (templates.data.length == 1) {
					this.SelectedReportTemplate = templates.data[0];
				}

				// store the return templates
				this.ReportTemplates = templates.data;
			}
		}

		this.CurrentUserId = this.getUserId;

		// set timezone based on user browser
		this.TimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;


		// get a list of the groups (Areas) that the user has access to
		await this.fetchUserGroups();

		// store them to our variable
		this.availableAreas = this.userGroups;

		// grab the HTML element for the editor row
		this.EditorRow = document.getElementById("edit-row");

		// get the activity log and start the timeout to refresh the data
		this.getActivityLogListLive();
	}

	private setLoadingStatus(status: boolean) {
		this.loading = status;
	}

	private async getActivityLogList() {
		this.setLoadingStatus(true);

		let queryStr = `?page=${this.PageNumber}`;

		// set the filter Id if we are filtering on a specific site
		if (this.FilteredGroupId > 0) {
			queryStr += `&areaId=${this.FilteredGroupId}`;
		}

		// add date filters if we are using the date search and not live data
		if (!this.liveData && this.fromDateValue && this.toDateValue) {
			queryStr += `&fromDate=${this.fromDateValue}&toDate=${this.toDateValue}`;
		}

		try {
			// request the data
			let result = await this.getActivityLog(queryStr);

			// results and paging data returned from API
			this.TableData = result.data.activityLogs as ActivityLogDTO[];
			this.TotalPages = result.data.totalPages;
			this.PageNumber = result.data.currentPage;

			this.setLoadingStatus(false);

			// create a list of the dates from the Log, used to display the date range we are displaying
			let dates = this.TableData.map(function(item) {
				return new Date(item.logged);
			});

			// get the min and max date for the data we have (displayed in the UI)
			if (dates && dates.length > 0) {
				let minDate = Math.min.apply(null, dates);
				let maxDate = Math.max.apply(null, dates);

				// send the data to the parent component (ActivityLogManagment)
				this.$emit("DateRange", {
					minDate: new Date(minDate) + "Z",
					maxDate: new Date(maxDate) + "Z"
				});
			}

			// update the available filter tabs based on our data
			this.SetFilters(this.TableData);
		} catch (err) {
			this.setLoadingStatus(false);
		}
	}

	private SetFilters(tabledata) {
		let sites: SelectedSite[] = [];

		// create a list of all the sites the user has access to, to be used as filters
		this.availableAreas.forEach(area => {
			if (area.groupTypeID == 1) {
				let site: SelectedSite = { areaID: area.groupID, title: area.title}
				sites.push(site);
			}
		});

		this.FilterableAreas = sites;
	}

	private getActivityLogListLive() {
		if (this.$route && this.$route.name == "activity-log") {
			// clear any current timeouts that are running
			if (this.timeOut != null) {
				clearTimeout(this.timeOut);
			}

			// only refresh the data if we not adding or editing a row, or not set to view live data
			if (!this.EditingRecord && this.liveData) {
				this.getActivityLogList();

				// create the timeout to refresh the data
				this.timeOut = setTimeout(() => {
					this.getActivityLogListLive();
				}, this.updateInterval);
			}
		}
	}

	// resets our edit object to default values ready to be reused
	private resetEditRowObject() {
		this.EditRowData.activityLogID = 0;
		this.EditRowData.eventTypeID = null;
		this.EditRowData.logged = new Date().toISOString();
		this.EditRowData.start = new Date().toISOString();
		this.EditRowData.end = null;
		this.EditRowData.categoryID = null;
		this.EditRowData.categoryTitle = null;
		this.EditRowData.areaID = null;
		this.EditRowData.areaTitle = null;
		this.EditRowData.viewedUserID = null;
		this.EditRowData.outcomeNote = null;
		this.EditRowData.hiddenFromUsers = false;

		// reset category data as this is based on the selected area
		this.CategoryData = [];
	}

	// takes the data from the matching log Id and makes it editable
	private async ShowActivityLogEditor(logID: number) {
		this.resetEditRowObject();

		this.CurrentActivityBeingEdited = logID;

		// get the HTML element of the row we want to edit
		let targetRow = document.getElementById("table-row-" + logID);

		// request a copy of the log from the API so were sure we have the latest version
		let record = this.getActivityLogEntry(logID).then(result => {
			this.EditRowData = result.data;

			this.setCategoryOptions(this.EditRowData.areaID);

			this.EditRowData.start = this.checkDateStringContainsZ(this.EditRowData.start);
			this.EditRowData.end = this.checkDateStringContainsZ(this.EditRowData.end);

			// display the edit row interface after the selected row in the DOM
			// the selected row will be hidden
			targetRow.after(this.EditorRow);

			this.EditingRecord = true;
		});
	}

	// copy the updated data from our edit interface
	private async UpdateActivityLog(rowId) {
		let newobj: ActivityLogDTO = {
			activityLogID: this.EditRowData.activityLogID,
			start: this.EditRowData.start,
			end: this.EditRowData.end,
			categoryID: this.EditRowData.categoryID,
			areaID: this.EditRowData.areaID,
			outcomeNote: this.EditRowData.outcomeNote,
			eventTypeID: 11,
			logged: new Date(),
			hiddenFromUsers: false,
			categoryTitle: this.EditRowData.categoryTitle,
			areaTitle: this.EditRowData.areaTitle,
			viewedUserID: 0,
			userTitle: this.EditRowData.userTitle
		};

		// hack to prevent issues with Axios not liking empty properties and messing update
		Object.keys(newobj).forEach(function(key, index) {
			if (newobj[key] == null) {
				delete newobj[key];
			}
		});

		if (!newobj.areaID) {
			alert("Please select an area for this activity log entry");
		} else if (!newobj.outcomeNote || newobj.outcomeNote == "") {
			alert("Please enter a note for this Activity Log entry");
		} else {
			if (rowId == 0) {
				await this.addActivityLog(newobj);
			} else {
				await this.editActivityLog(newobj);
			}

			this.EditingRecord = false;

			this.CurrentActivityBeingEdited = 0;

			this.resetEditRowObject();

			// refresh list after updating
			await this.getActivityLogList();
		}
	}

	private CancelLogUpdate() {
		this.CurrentActivityBeingEdited = 0;
		this.EditingRecord = false;
		this.resetEditRowObject();
		this.getActivityLogListLive();
	}

	private LogNewActivity() {
		this.resetEditRowObject();

		// find the HTML element for the table
		let table = document.getElementById("activity-log-table");
		this.EditingRecord = true;
		this.CurrentActivityBeingEdited = 0;

		// if we are filtering on a site, default the selected area to that site
		if (this.FilteredGroupId > 0) {
			let area = this.availableAreas.find(obj => obj.groupID == this.FilteredGroupId);
			this.setSelectedArea(area);
		}

		// show the editor row at the top of the table
		table.prepend(this.EditorRow);

		// automatically focus on the outcome note column
		this.$nextTick(() => {
			document.getElementById("outcome-note").focus();
		});
	}

	private async ToggleHideFromUserStatus(id: number, status: boolean) {
		// make API to show or hide the activity log
		status ? await this.hideActivityLog(id) : await this.unHideActivityLog(id);

		// refresh data
		await this.getActivityLogList();
	}

	private async setSelectedArea(area) {
		this.EditRowData.areaID = area.groupID;
		this.EditRowData.areaTitle = area.title;

		this.setCategoryOptions(area.groupID);
	}

	private setSelectedCategory(category) {
		this.EditRowData.categoryID = category.eventOutcomeID;
		this.EditRowData.categoryTitle = category.title;
	}

	private async setSelectedReportTemplate(template) {
		this.SelectedReportTemplate = template;
	}

	// get the time value from date time object for the editor
	private getTime(field) {
		let dateTime = this.EditRowData[field];

		let time = formatDateStr(dateTime, true, true, false);

		return time;
	}

	// create a date object from the time in the editor
	private setTime(event, field) {
		let time = event.target.value;
		let value = null;
		if (time) {
			let dateTime = new Date();

			let hoursMins = time.split(":");

			dateTime.setHours(hoursMins[0]);
			dateTime.setMinutes(hoursMins[1]);

			value = dateTime.toISOString();
		}

		this.EditRowData[field] = value;
	}

	private async setCategoryOptions(areaID) {
		if (areaID) {
			this.CategoryData = [];

			// get category options for Area from API
			await this.getActivityLogOutcomes(areaID).then(result => {
				this.CategoryData = result.data;
			});
		}
	}

	// check that our time string contains Z for UTC
	private checkDateStringContainsZ(date) {
		if (date && !date.toString().includes("Z")) {
			return date.toString() + "Z";
		}

		return date;
	}

	// set the page number and request records for that page
	private async paginate(pageNumber: number) {
		this.EditingRecord = false;
		this.$emit("LiveStatusChanged", false);
		this.PageNumber = pageNumber;
		await this.getActivityLogList();
	}

	// set the starting date range to use when downloading a report
	private get ReportDownloadStartDate() {
		if (!this.liveData) {
			return this.fromDate;
		}

		let start = new Date();
		start.setHours(0, 0, 0, 0);

		return start.toUTCString();
	}

	// set the ending date range to use when downloading a report
	private get ReportDownloadFromDate() {
		if (!this.liveData) {
			return this.toDate;
		}

		let end = new Date();
		end.setHours(23, 59, 59, 999);

		return end.toUTCString();
	}

	// generate url to request the report PDF
	private get DownloadActivityLogReportURL() {
		if (this.SelectedReportTemplate != null) {
			let href = `${this.ReportEndPoint}/Reports/DownloadReportPDF/ShiftReport/${this.SelectedReportTemplate.reportTemplateID}?shifts=1&startDate=${this.ReportDownloadStartDate}&endDate=${this.ReportDownloadFromDate}&timeZone=${this.TimeZone}&fileName=ActivityLog-${this.FilteredGroupTitle}-${this.ReportDownloadFilename}`;

			let groupId = this.FilteredGroupId > 0 ? this.FilteredGroupId : this.getUserGroupId;

			href += `&groupId=${groupId}`;

			return href;
		}

		return "";
	}

	private selectFilter(areaId) {
		// if the editor is open then close it
		this.CancelLogUpdate();

		this.FilteredGroupId = areaId;

		if (this.FilteredGroupId == 0) {
			this.FilteredGroupTitle = "All";
		} else {
			let area = this.availableAreas.filter(area => {
				return area.groupID == areaId;
			})[0];
			this.FilteredGroupTitle = area.title;
		}

		// refresh data
		this.getActivityLogList();
	}

	// check if all the Area ids in the results are the same, if so we hide the Area column in the table
	private get AllRowsAreSameArea() {
		let id = this.FilteredGroupId;
		let areSame =
			this.TableData.length &&
			this.TableData.every(row => {
				return row.areaID == id;
			});

		return areSame;
	}

	// generate filename to used for the report download
	private get ReportDownloadFilename() {
		return this.liveData ? new Date().toISOString().split("T")[0] : this.fromDate.toString().split("T")[0];
	}

	// Handles the '+' Add site (tab) being clicked which should show the site selection modal
	private showSiteSelectorModal(shown:boolean) {
		this.SiteSelectorModalShown = shown;
	}

	// Handles the tab being closed
	private closeTab(selectedSite: SelectedSite) {
		this.removeSelectedSite(selectedSite);
	}

	// Handler for a site being selected (Clicked) in the site tree within the modal
	private siteSelected(selectedSiteTreeNode: SiteTreeNode) {
		// If a tab for the site is not already shown, add it to the array of shown sites
		if (this.selectedSites.filter(s => s.areaID == selectedSiteTreeNode.group.groupID).length == 0) {
			let selectedSite: SelectedSite = {
				areaID: selectedSiteTreeNode.group.groupID,
				title: selectedSiteTreeNode.group.title
			};
			this.addSelectedSite(selectedSite);
		}

		// Switch to the selected tab 
		this.selectFilter(selectedSiteTreeNode.group.groupID);
		// Close the modal
		this.showSiteSelectorModal(false);
	}
}
